import { useEffect, useRef, useState } from "react";

import { BaseInput, Checkbox } from "@smart/itops-sb-design-system-dom";
import { isNullOrUndefined, partition } from "@smart/itops-utils-basic";

import {
  DynamicOptionsPlaceholder,
  shouldShowDynamicOptionsPlaceholder,
} from "./dynamic-options-placeholder";
import { FieldGroup } from "./field-group";
import { FieldSet } from "./input";
import { optionsWithFallback } from "./item";
import { Label } from "./label";
import { FieldProps, FieldWrapper } from "./wrapper";
import { fieldName } from "../../hooks";
import { getUITranslation } from "../../utils/ui-translations";

export const CheckboxField = ({
  field,
  index,
  innerRef,
  value,
  error,
  loading,
  disabled,
  onChange,
  onBlur,
  isPreview,
  selectedLanguage,
}: FieldProps<HTMLInputElement>) => {
  const firstItemRef = useRef<HTMLInputElement>(null);
  const otherRef = useRef<HTMLInputElement>(null);

  const fieldOptionsWithFallback = optionsWithFallback(field.options);
  const [selected, otherValues] = partition(
    Array.isArray(value) ? value : [value],
    (s) => !!fieldOptionsWithFallback.find((o) => o.value === s),
  );
  const otherValue = otherValues.join(", ") || undefined;
  const [other, setOther] = useState(() => !!otherValue);
  const id = fieldName({ field, index });
  const errorId = fieldName({ field, index, suffix: "error" });

  useEffect(() => {
    if (isNullOrUndefined(value)) setOther(false);
  }, [value]);

  const handleCheckboxChange = (checked: boolean, val?: string) => {
    let updateValues: string[] = [];
    if (checked) {
      if (val) updateValues = [...selected, val];
    } else {
      updateValues = selected.filter((s) => s !== val);
    }
    if (otherValue) {
      updateValues = [
        ...updateValues.filter((v) => v !== otherValue),
        otherValue,
      ];
    }
    onChange(updateValues);
    onBlur();
  };

  if (shouldShowDynamicOptionsPlaceholder(field)) {
    return (
      <DynamicOptionsPlaceholder
        field={field}
        index={index}
        error={error}
        loading={loading}
        disabled={disabled}
        isPreview={isPreview}
      />
    );
  }

  return (
    <FieldWrapper disabled={disabled} isLoading={loading}>
      <FieldGroup
        id={id}
        label={<Label {...field} hint={field.hint ?? undefined} />}
      >
        <FieldSet
          role="radiogroup"
          uri={field.uri}
          error={error}
          errorId={errorId}
        >
          <input
            ref={innerRef}
            id={id}
            value={selected.join(", ") ?? ""}
            className="hidden"
            aria-invalid={!!error}
            aria-errormessage={error && errorId}
            readOnly
            onFocus={() => {
              if (other) {
                otherRef.current?.focus();
              } else {
                firstItemRef.current?.focus();
              }
            }}
          />
          {fieldOptionsWithFallback.map((option, idx) => (
            <Checkbox
              key={option.value || idx}
              {...option}
              checked={selected.includes(option.value)}
              disabled={disabled}
              error={!!error}
              onChange={handleCheckboxChange}
              onEventChange={undefined}
            />
          ))}
          {field.allowCustomResponse && (
            <>
              <Checkbox
                label={getUITranslation("other", selectedLanguage)}
                value="other"
                disabled={disabled}
                checked={other}
                error={!!error}
                onChange={() => {
                  if (!other) {
                    setOther(true);
                    otherRef.current?.focus();
                  } else {
                    setOther(false);
                    onChange(selected);
                    onBlur();
                  }
                }}
              />
              {other && (
                <BaseInput
                  ref={otherRef}
                  name={fieldName({ field, index, suffix: "other-input" })}
                  type="text"
                  autoFocus
                  value={otherValue ?? ""}
                  aria-hidden={!other}
                  aria-label={getUITranslation("otherValue", selectedLanguage)}
                  className="ml-[2.8rem]"
                  disabled={disabled}
                  onChange={(e) => {
                    if (e.currentTarget.value) {
                      onChange([...selected, e.currentTarget.value]);
                    } else {
                      onChange(selected);
                    }
                  }}
                  onBlur={onBlur}
                />
              )}
            </>
          )}
        </FieldSet>
      </FieldGroup>
    </FieldWrapper>
  );
};
